import type { Translation } from '../types';

export const ko: Translation = {
  meta: { name: '한국어', english: 'Korean', tag: 'ko', dir: 'ltr' },

  common: {
    menu: "메뉴",
    cancel: '취소',
    done: '완료',
    join: '참가',
    leave: '나가기',
    refresh: '새로고침',
    save: '저장',
    you: '나',
    host: '방장',
    ai: 'AI',
    ready: '준비 완료',
    notReady: '준비 안 됨',
  },

  conn: {
    live: '연결됨',
    connecting: '연결 중',
    offline: '오프라인',
    connectingFull: '연결 중…',
    reconnectBanner: '연결이 끊겼습니다. 다시 연결 중이며 자리는 3분간 유지됩니다.',
  },

  nickname: {
    pickTitle: '이름을 정하세요',
    changeTitle: '이름 변경',
    subtitle: '다른 플레이어에게 이 이름으로 표시됩니다.',
    placeholder: '예: 와일드 포',
    label: '닉네임',
    letsPlay: '시작하기',
  },

  lobby: {
    heroTitle: '친구와 함께 UNO',
    heroSubtitle: '가입 없이 링크만 공유하면 시작.',
    createRoom: '방 만들기',
    roomCode: '방 코드',
    publicRooms: '공개 방',
    empty: '아직 공개 방이 없습니다. 첫 번째 방을 만들어 보세요.',
    cardMeta: '방장 {host} · 턴당 {seconds}초',
    stacking: '중첩 허용',
    badgeOpen: '참가 가능',
    badgeFull: '가득 참',
    badgeLive: '게임 중',
    lockedTitle: '{id} 방은 잠겨 있습니다',
    password: '비밀번호',
    passwordLabel: '방 비밀번호',
  },

  create: {
    title: '새 방',
    roomName: '방 이름',
    roomNamePlaceholder: '금요일 밤 UNO',
    maxPlayers: '최대 인원',
    playersOption: { other: '{count}명' },
    passwordOptional: '비밀번호 (선택)',
    passwordPlaceholder: '비워 두면 누구나 참가 가능',
    listPublic: '공개 방 목록에 표시',
    houseRules: '하우스 룰',
    submit: '만들기',
  },

  rules: {
    turnTimer: '턴 제한 시간',
    turnTimerHint: '시간이 다 되면 봇이 대신 낼 수 있는 카드를 무작위로 냅니다.',
    startingHand: '시작 손패',
    unoPenalty: 'UNO 벌칙',
    stackDrawTwo: '+2 중첩',
    stackDrawTwoHint: '+2에 +2로 받아쳐 카드를 뽑지 않습니다',
    stackDrawFour: '+4 중첩',
    stackDrawFourHint: '+4에 +4로 받아칠 수 있습니다',
    challengeWildFour: '+4 챌린지',
    challengeWildFourHint: '다음 플레이어가 부당한 +4를 지적할 수 있습니다',
    strictWildFour: '엄격한 +4',
    strictWildFourHint: '현재 색 카드가 없을 때만 낼 수 있습니다',
    playDrawn: '뽑은 카드 바로 내기',
    playDrawnHint: '뽑은 카드를 즉시 낼 수 있습니다',
    drawUntilPlayable: '낼 수 있을 때까지 뽑기',
    drawUntilPlayableHint: '낼 수 있는 카드가 나올 때까지 계속 뽑습니다',
    hostOnly: '방장만 변경할 수 있습니다',
  },

  room: {
    copyLink: '초대 링크 복사',
    copied: '✓ 복사됨',
    roomLabel: '방',
    locked: '잠김',
    public: '공개',
    unlisted: '비공개',
    readyStatus: '{total}명 중 {ready}명 준비 완료',
    botsJoining: { other: '봇 {count}명 참가 예정' },
    startsWhenReady: '모두 준비되면 바로 시작합니다.',
    needMorePlayers: '시작하려면 {min}명이 필요합니다. 친구를 초대하거나 봇을 추가하세요.',
    playersTitle: '플레이어',
    botsTitle: 'AI 봇',
    botsHostOnly: '방장만 변경할 수 있습니다',
    noBots: '봇 없음',
    botsWillJoin: { other: '봇 {count}명이 참가합니다' },
    botsHint: '게임이 시작되면 자리에 앉습니다. 현재 최대 {max}명까지 가능합니다.',
    botsRoomFull: '방이 가득 차 봇을 추가할 수 없습니다.',
    soloCta: '혼자 하시나요? 봇을 추가하고 시작하세요',
    addBot: '봇 추가',
    removeBot: '봇 줄이기',
    removePlayer: '{name} 내보내기',
    botPlaceholder: '봇 {n}',
    defaultRoomName: '{name}님의 방',
    imReady: '준비 완료',
    cancelReady: '준비 취소',
    offlineTag: '오프라인',
  },

  game: {
    yourTurn: '내 차례',
    yourTurnStack: '내 차례 — 중첩하거나 +{count}장 뽑기',
    othersTurn: '{name}의 차례',
    waiting: '대기 중…',
    gameOver: '게임 종료',
    challengeWindow: '와일드 드로우 포 — 챌린지 시간',
    drawPileLeft: '{count}장 남음',
    discard: '버린 패',
    draw: '뽑기',
    takeCards: '+{count}장 받기',
    pass: '넘기기',
    uno: 'UNO!',
    judge: '지적',
    cardCount: { other: '{count}장' },
    clockwise: '시계 방향',
    counterClockwise: '반시계 방향',
    activeColour: '현재 색',
    noCards: '손패 없음 — 훌륭합니다.',
    oneCard: '한 장 남음!',
    unoTag: 'UNO',
    forfeitedTag: '기권',
    offlineFor: '오프라인 {seconds}초',
    placeTag: '{place}위',
    pickColour: '색을 고르세요',
    challengeTitle: '{name}이(가) 와일드 드로우 포를 냈습니다. 챌린지할까요?',
    challengeExplain: '허풍이었다면 상대가 {bluff}장을 뽑습니다. 아니라면 내가 {fail}장을 뽑습니다.',
    challengeBtn: '챌린지',
    acceptBtn: '+{count}장 받기',
    youWin: '🎉 승리!',
    standingsTitle: '게임 종료',
    points: '{points}점',
    forfeited: '기권',
    backToLobby: '로비로',
    playAgain: '다시 하기',
    waitingOthers: '다른 플레이어를 기다리는 중…',
    faceDownCard: '뒷면 카드',
    sortByColour: "색으로 정렬",
    sortAsDealt: "받은 순서 유지",
    drawACard: '카드 한 장 뽑기',
    drawNCards: '카드 {count}장 뽑기',
  },

  events: {
    someone: '누군가',
    unoCalledYou: 'UNO를 외쳤습니다!',
    unoCalledOther: '{name}이(가) UNO를 외쳤습니다!',
    judged: '{accuser}이(가) {target}을(를) 잡았습니다 — +{count}장',
    judgedYou: '{accuser}에게 잡혔습니다 — +{count}장',
    challengeBluff: '허풍 적발 — {name}이(가) {count}장 뽑습니다',
    challengeFailed: '챌린지 실패 — {name}이(가) {count}장 뽑습니다',
    forfeited: '{name}이(가) 기권했습니다.',
    afkYou: '시간이 초과되어 자동으로 냈습니다.',
    afkOther: '{name}의 시간이 초과되어 자동으로 냈습니다.',
    hostChanged: '{name}이(가) 새 방장이 되었습니다.',
    reshuffled: '뽑기 더미를 다시 섞었습니다.',
    youWin: '승리했습니다!',
    winner: '{name} 승리!',
    gameOver: '게임이 끝났습니다.',
  },

  errors: {
    bad_request: '문제가 발생했습니다.',
    no_session: '세션이 만료되어 다시 연결하는 중입니다.',
    room_not_found: '그런 방이 없습니다.',
    room_full: '그 방은 가득 찼습니다.',
    wrong_password: '비밀번호가 틀렸습니다.',
    already_in_room: '먼저 현재 방에서 나가세요.',
    not_in_room: '방에 참가하고 있지 않습니다.',
    not_host: '방장만 할 수 있습니다.',
    game_in_progress: '그 게임은 이미 시작되었습니다.',
    not_enough_players: '인원이 부족합니다.',
    stale_action: '다른 플레이어가 먼저 행동해 요청이 취소되었습니다.',
    illegal_action: '허용되지 않는 동작입니다.',
    rate_limited: '너무 빠릅니다.',
    nickname_invalid: '사용할 수 없는 닉네임입니다.',
  },

  theme: {
    title: '카드 테마',
    description: '이미지가 없으면 내장 CSS 카드로 대체됩니다.',
    builtIn: '내장 — 이미지 파일 불필요',
    pngHint: '300×450 PNG를 {path}에 넣고 테마 목록에 등록하세요.',
  },

  language: {
    title: '언어',
    description: '즉시 적용되며 이 기기에 저장됩니다.',
  },

  cards: {
    red: "빨강",
    yellow: "노랑",
    green: "초록",
    blue: "파랑",
    wild: "와일드",
    skip: "스킵",
    reverse: "리버스",
    draw2: "+2",
    wildCard: "와일드",
    wild4: "+4",
  },

  log: {
    title: "플레이 로그",
    empty: "아직 없습니다.",
    close: "로그 숨기기",
    timestamps: "시각 표시",
    chatPlaceholder: "한마디 하기…",
    send: "보내기",
    played: "{name}이(가) {card}을(를) 냈습니다",
    drew: "{name}이(가) {count}장 뽑았습니다",
    skipped: "{name}이(가) 건너뛰었습니다",
    reversed: "순서가 바뀌었습니다",
    joined: "{name}이(가) 참가했습니다",
    left: "{name}이(가) 나갔습니다",
    started: "게임 시작",
  },

  profile: {
    avatar: "아바타",
    avatarHint: "아바타를 고르거나 이니셜을 그대로 두세요.",
  },

  leave: {
    title: "게임에서 나갈까요?",
    body: "아직 진행 중입니다. 나가면 기권 처리되고 손패는 더미로 돌아갑니다.",
    stay: "계속하기",
    confirm: "나가기",
    unload: "게임이 진행 중입니다.",
  },
};
